import { useEffect, useState } from "react";
import type { SceneNodeInfo, TransformControlsSettings, TransformMode } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { CompactCheck, CompactSelect, CompactSlider, CompactNumber, MiniBtn, Row } from "../../inspector/rows";
// Shared with the v58 node inspector: the gizmo and the inspector must resolve
// a selection to the SAME node.
import { findNodeByGroupId } from "./nodeLookup";

/** Transform gizmo + pivot controls (v14). The gizmo attaches to a scene node;
 * webgi's TransformControlsPlugin auto-attaches to whatever's picked, so this
 * mirrors that: enabling the gizmo (or picking a new part while it's already
 * enabled) resolves the selected material-group to its scene-tree node and
 * attaches there. "Attach to Model" still targets the loaded model's root so
 * the whole piece can be moved/rotated/scaled. Pivot buttons re-pivot the
 * attached node. */
export function TransformControl() {
  const { client } = useViewer();
  const r = client.ring;
  const selection = useDomainState("selection") as string | null;
  const [s, setS] = useState<TransformControlsSettings | null>(null);
  const [attached, setAttached] = useState<string | null>(null);

  useEffect(() => {
    r.getTransformControls().then(setS).catch(() => {});
  }, [r]);

  // webgi parity: while the gizmo is enabled, attach it to whatever part is
  // currently selected (falls back to nothing selected -> detach).
  useEffect(() => {
    if (!s?.enabled) return;
    if (!selection) {
      void r.attachTransformControls(null);
      setAttached(null);
      return;
    }
    void r.getSceneTree().then((tree) => {
      const node = findNodeByGroupId(tree, selection);
      if (node) {
        void r.attachTransformControls(node.id);
        setAttached(node.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, s?.enabled]);

  if (!s) return null;
  const set = (p: Partial<TransformControlsSettings>) => r.setTransformControls(p).then(setS);

  const rootNode = async (): Promise<SceneNodeInfo | null> => {
    const tree = await r.getSceneTree();
    return tree[0] ?? null;
  };

  return (
    <>
      <Row label="Gizmo"><CompactCheck checked={s.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Mode">
        <CompactSelect
          value={s.mode}
          options={[
            { id: "translate", label: "Translate" },
            { id: "rotate", label: "Rotate" },
            { id: "scale", label: "Scale" },
          ]}
          onChange={(v) => void set({ mode: v as TransformMode })}
        />
      </Row>
      <Row label="Space">
        <CompactSelect
          value={s.space}
          options={[{ id: "world", label: "World" }, { id: "local", label: "Local" }]}
          onChange={(v) => void set({ space: v as "world" | "local" })}
        />
      </Row>
      <Row label="Size"><CompactSlider value={s.size} min={0.1} max={10} step={0.1} onChange={(v) => void set({ size: v })} /></Row>
      <Row label="Translate Snap"><CompactNumber value={s.translationSnap ?? 0} min={0} step={0.1} onChange={(v) => void set({ translationSnap: v > 0 ? v : null })} /></Row>
      <Row label="Rotate Snap (°)"><CompactNumber value={s.rotationSnapDeg ?? 0} min={0} max={90} step={1} onChange={(v) => void set({ rotationSnapDeg: v > 0 ? v : null })} /></Row>
      <Row label="Show X"><CompactCheck checked={s.showX} onChange={(v) => void set({ showX: v })} /></Row>
      <Row label="Show Y"><CompactCheck checked={s.showY} onChange={(v) => void set({ showY: v })} /></Row>
      <Row label="Show Z"><CompactCheck checked={s.showZ} onChange={(v) => void set({ showZ: v })} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1">
        <MiniBtn onClick={async () => { const n = await rootNode(); if (n) { await r.attachTransformControls(n.id); setAttached(n.id); } }}>Attach Model</MiniBtn>
        <MiniBtn onClick={() => { void r.attachTransformControls(null); setAttached(null); }}>Detach</MiniBtn>
      </div>
      {attached ? (
        <div className="flex flex-wrap gap-1.5 px-3 py-1">
          <MiniBtn onClick={() => r.setPivot(attached, "center")}>Pivot Center</MiniBtn>
          <MiniBtn onClick={() => r.setPivot(attached, "bottom")}>Pivot Bottom</MiniBtn>
          <MiniBtn onClick={() => r.setPivot(attached, "origin")}>Pivot Origin</MiniBtn>
        </div>
      ) : null}
    </>
  );
}
